/**
 * ss-products-store — shared loader + filter + score module
 * Used by both ss-comparison-table (Plan 4b) and ss-configurator (Plan 5)
 *
 * Data source: /assets/smart-glasses-data.json (56 products, parsed from xlsx)
 *
 * Public API (window.SsProducts):
 *   await SsProducts.load()                  → resolves dataset, cached
 *   SsProducts.all()                         → all 56 products
 *   SsProducts.sellable()                    → only sellable_at_smartsenses=true
 *   SsProducts.filter(predicates)            → array of products matching ALL predicates
 *   SsProducts.score(answers, products)      → ranked products for configurator
 *   SsProducts.priceEUR(p)                   → USD-to-EUR converted (approx 0.92)
 */

(function () {
  if (window.SsProducts) return;

  const USD_TO_EUR = 0.92;
  const DATA_URL = window.__ssProductsDataUrl || '/assets/smart-glasses-data.json';

  let _cache = null;
  let _loadingPromise = null;

  async function load() {
    if (_cache) return _cache;
    if (_loadingPromise) return _loadingPromise;
    _loadingPromise = fetch(DATA_URL, { credentials: 'omit' })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load ${DATA_URL}: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        _cache = data;
        return data;
      });
    return _loadingPromise;
  }

  function all() {
    return _cache ? _cache.products : [];
  }

  function sellable() {
    return all().filter((p) => p.sellable_at_smartsenses === true);
  }

  /**
   * Generic filter: pass an object of predicate functions.
   * Each predicate takes a product, returns truthy to include.
   *
   * filter({
   *   price: (p) => p.price_usd != null && p.price_usd <= 200,
   *   hasTranslation: (p) => p.translation === true,
   * })
   */
  function filter(predicates = {}) {
    const products = all();
    const fns = Object.values(predicates).filter(
      (fn) => typeof fn === 'function'
    );
    return products.filter((p) => fns.every((fn) => fn(p)));
  }

  /**
   * Score products against configurator answers.
   * answers = {
   *   primaryPriority: 'camera' | 'translation' | 'discreet' | 'budget' | 'display',
   *   useCase: 'commute' | 'sport' | 'work' | 'industry' | 'travel',
   *   budgetEur: { min: number, max: number },
   *   mustHave: { camera, ai, translation, waterproof, rx } (booleans)
   * }
   *
   * Returns top-N products (default 3), with .score and .reasons fields added.
   */
  function score(answers, options = {}) {
    const limit = options.limit || 3;
    const onlySellable = options.onlySellable !== false; // default true
    const universe = onlySellable ? sellable() : all();

    const max = (answers.budgetEur && answers.budgetEur.max) || Infinity;
    const min = (answers.budgetEur && answers.budgetEur.min) || 0;

    return universe
      .map((p) => {
        let s = 0;
        const reasons = [];

        // Must-have hard filters (drop to score 0 if fail)
        if (answers.mustHave) {
          if (answers.mustHave.camera && !p.camera_mp) return { p, score: 0, reasons: [] };
          if (answers.mustHave.ai && !p.ai_voice) return { p, score: 0, reasons: [] };
          if (answers.mustHave.translation && !p.translation) return { p, score: 0, reasons: [] };
          if (answers.mustHave.rx && !(p.use_cases || []).includes('rx_lenses')) return { p, score: 0, reasons: [] };
          if (answers.mustHave.waterproof) {
            const wr = (p.water_resistance || '').toUpperCase();
            if (!wr.match(/IP[6-9X]|IPX[4-9]/)) return { p, score: 0, reasons: [] };
          }
        }

        // Budget filter
        const priceEUR = p.price_usd != null ? p.price_usd * USD_TO_EUR : null;
        if (priceEUR != null) {
          if (priceEUR > max || priceEUR < min) return { p, score: 0, reasons: [] };
        }

        // Use case match (+30)
        if (answers.useCase && (p.use_cases || []).includes(answers.useCase)) {
          s += 30;
          reasons.push(`Passend für ${ucLabel(answers.useCase)}`);
        }

        // Primary priority weighting (+40)
        const pri = answers.primaryPriority;
        if (pri === 'camera' && p.camera_mp) {
          s += Math.min(40, p.camera_mp * 3);
          reasons.push(`${p.camera_mp}MP Kamera`);
        } else if (pri === 'translation' && p.translation) {
          s += 40;
          reasons.push('Echtzeit-Übersetzung');
        } else if (pri === 'discreet') {
          s += (p.discreet || 0) * 8;
          if (p.discreet >= 4) reasons.push('Sehr dezent');
        } else if (pri === 'budget' && priceEUR != null) {
          // lower price = higher score
          s += Math.max(0, 40 - priceEUR / 10);
          reasons.push(`€${Math.round(priceEUR)} Preisrange`);
        } else if (pri === 'display' && p.display) {
          s += 40;
          reasons.push('Mit Display');
        }

        // Sellability bonus (+15)
        if (p.sellable_at_smartsenses) {
          s += 15;
          reasons.push('Bei SmartSenses verfügbar');
        }

        // Availability bonus (+10)
        if (p.delivery_status === 'Available') s += 10;
        else if (p.delivery_status === 'Pre-order') s += 5;

        return { p, score: s, reasons };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map((x) => ({ ...x.p, _score: x.score, _reasons: x.reasons }));
  }

  function ucLabel(uc) {
    return {
      commute: 'Pendeln',
      sport: 'Sport',
      work: 'Arbeit',
      industry: 'Industrie',
      travel: 'Reisen',
      rx_lenses: 'Sehstärke',
    }[uc] || uc;
  }

  function priceEUR(p) {
    // Prefer an explicit EUR price (used for SmartSenses-sellable rows
    // where the live storefront EUR price is the source of truth).
    if (p.price_eur != null) return Math.round(p.price_eur);
    if (p.price_usd == null) return null;
    return Math.round(p.price_usd * USD_TO_EUR);
  }

  window.SsProducts = { load, all, sellable, filter, score, priceEUR, ucLabel };
})();
